/**
 * Universities domain — utility functions and shared types.
 *
 * Re-exports generated entity types plus the typed adapters that bridge the
 * Kubb-generated shapes to the legacy props expected by `DECommonTable`,
 * `DemographicsSection`, etc. The cross-domain adapters live here (rather
 * than in component files) so the casts are documented and centralized.
 */

import type { UniversityChainProxy } from '../../models/universities'
import type {
  StandardPaginatedResponse,
  StandardSummaryResponse,
} from '../../models/paginated-response'
import type { AddressInfo } from '../../models/restaurant'
import { getUniversitiesSummary as getUniversitiesSummaryClient } from '../generated/clients/getUniversitiesSummary'
import { listUniversities as listUniversitiesClient } from '../generated/clients/listUniversities'
import type { GetUniversitiesSummaryQueryParams } from '../generated/types/GetUniversitiesSummary'
import type { ListUniversitiesQueryParams } from '../generated/types/ListUniversities'
import type { UniversityNested } from '../generated/types/UniversityNested'

// ── Domain entity type re-exports ───────────────────────────────────

export type { UniversityListItem } from '../generated/types/UniversityListItem'
export type { UniversityNested } from '../generated/types/UniversityNested'
export type { PaginatedUniversityListItemList } from '../generated/types/PaginatedUniversityListItemList'

// ── Typed adapters for legacy component props ───────────────────────

/**
 * List universities shaped for `DECommonTable.apiGetListAction`.
 *
 * The table component is generic over `UniversityChainProxy` (the legacy
 * model the column definitions are typed against). At runtime the generated
 * `UniversityListItem` has the same fields the columns read, so the cast is
 * a type-only bridge — encapsulated here so call sites stay clean.
 */
export async function listUniversitiesForTable(
  params: ListUniversitiesQueryParams = {}
): Promise<StandardPaginatedResponse<UniversityChainProxy>> {
  return listUniversitiesClient(
    params
  ) as unknown as StandardPaginatedResponse<UniversityChainProxy>
}

/**
 * Get the universities summary count shaped for
 * `DECommonTable.apiGetSummaryAction`.
 *
 * The legacy `StandardSummaryResponse` has a second `alt_count` field the
 * universities backend never populates; the table treats the missing value
 * as `undefined`. We build the response explicitly and default `alt_count`
 * to 0 — the same value the shared table plumbing falls back to.
 */
export async function getUniversitiesSummaryForTable(
  params: GetUniversitiesSummaryQueryParams = {}
): Promise<StandardSummaryResponse> {
  const res = await getUniversitiesSummaryClient(params)
  return { count: res.count, alt_count: 0 }
}

/**
 * Convert the generated `UniversityNested.address_info` (a `VerticalZipCode`
 * shape) into the legacy `AddressInfo` interface that `DemographicsSection`
 * accepts. The runtime data is compatible; this helper makes the type bridge
 * explicit instead of repeating the cast at each call site.
 */
export function toLegacyAddressInfo(
  addressInfo: UniversityNested['address_info'] | null | undefined
): AddressInfo | undefined {
  if (!addressInfo) return undefined
  return addressInfo as AddressInfo
}
